import React, { useEffect, useState } from "react";
import { Receipt, AlertCircle, CheckCircle2, Clock, Flag, Loader2, BadgeCheck, ImagePlus, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { ridesApi, errorMessage } from "../../lib/api";

const reasonLabel = (r) => (r === "no_show" ? "Driver no-show cancellation" : "You cancelled after a driver accepted");

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86400000);
};

function StatusRow({ f }) {
  if (f.status === "waived" || f.status === "refunded") {
    return (
      <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-bold text-green-700" data-testid={`fee-waived-${f.fee_id}`}>
        <BadgeCheck className="h-3 w-3" /> {f.status === "waived" ? "Waived — you won't be charged" : "Refunded after review"}
      </span>
    );
  }
  if (f.dispute_status === "open") {
    return <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-700" data-testid={`fee-under-review-${f.fee_id}`}><Clock className="h-3 w-3" /> Under review by support</span>;
  }
  const base = f.status === "collected"
    ? <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-bold text-green-700"><CheckCircle2 className="h-3 w-3" /> Collected on your next ride</span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700"><AlertCircle className="h-3 w-3" /> Will be added to your next ride</span>;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {base}
      {f.dispute_status === "resolved" && f.resolution === "upheld" && <span className="inline-flex items-center gap-1 rounded-full bg-surface-200 px-2 py-0.5 text-[11px] font-bold text-ink-soft" data-testid={`fee-upheld-${f.fee_id}`}>Reviewed — fee stands</span>}
    </div>
  );
}

export const CancellationFeesCard = ({ title = "Cancellation fees" }) => {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [appealId, setAppealId] = useState(null);
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => ridesApi.cancellationFees().then(setData).catch(() => setData({ outstanding: 0, fees: [] }));
  useEffect(() => { load(); }, []);

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image is too large (max 10 MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto({ file_data: String(reader.result).split(",")[1], mime: file.type, preview: reader.result });
    reader.readAsDataURL(file);
  };

  const submitDispute = async (feeId) => {
    if (reason.trim().length < 4) { toast.error("Please add a short reason."); return; }
    setBusy(true);
    try { await ridesApi.disputeFee(feeId, reason.trim(), photo ? [{ file_data: photo.file_data, mime: photo.mime }] : undefined); toast.success("Sent to support for review"); setOpenId(null); setReason(""); setPhoto(null); load(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const submitAppeal = async (feeId) => {
    if (reason.trim().length < 4) { toast.error("Please tell us why you still disagree."); return; }
    setBusy(true);
    try { await ridesApi.appealFee(feeId, reason.trim()); toast.success("Appeal sent for a final review"); setAppealId(null); setReason(""); load(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!data || !data.fees?.length) return null;
  return (
    <div className="rounded-2xl border border-surface-300 bg-white p-5" data-testid="cancellation-fees">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="inline-flex items-center gap-2 text-base font-bold text-ink"><Receipt className="h-4 w-4 text-brand" /> {title}</h3>
        {data.outstanding > 0 && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800" data-testid="cancellation-outstanding">₹{data.outstanding} outstanding</span>}
      </div>
      {(data.charged_total > 0 || data.waived_total > 0) && (
        <p className="mb-3 text-xs font-semibold text-ink-soft" data-testid="fairness-summary">₹{data.charged_total || 0} charged · ₹{data.waived_total || 0} waived</p>
      )}
      <ul className="divide-y divide-surface-200 text-sm">
        {data.fees.map((f) => (
          <li key={f.fee_id} className="py-3" data-testid={`cancel-fee-${f.fee_id}`}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-ink">{reasonLabel(f.reason)}</p>
              <span className="font-bold text-ink">₹{f.amount}</span>
            </div>
            <p className="mt-0.5 text-xs text-ink-muted">
              {f.pickup_address ? `${f.pickup_address}${f.drop_address ? ` → ${f.drop_address}` : ""}` : "Ride"} · {new Date(f.created_at).toLocaleDateString()}
            </p>
            <p className="mt-1 text-xs text-ink-soft">₹{f.flat_fee} base{f.wait_charge > 0 ? ` + ₹${f.wait_charge} waiting (${f.wait_min} min)` : ""}</p>
            <StatusRow f={f} />
            {f.final && <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-surface-200 px-2 py-0.5 text-[11px] font-bold text-ink-soft" data-testid={`fee-final-${f.fee_id}`}><Lock className="h-3 w-3" /> Final — decision closed</span>}
            {f.resolution_note && <p className="mt-1 text-xs italic text-ink-muted" data-testid={`resolution-note-${f.fee_id}`}>“{f.resolution_note}”</p>}
            {f.dispute_status === "resolved" && f.resolution === "upheld" && f.can_appeal && (
              appealId === f.fee_id ? (
                <div className="mt-2 space-y-2">
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Still disagree? Tell us why (one appeal allowed)" className="w-full rounded-lg border border-surface-300 p-2 text-xs" data-testid={`appeal-reason-${f.fee_id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => submitAppeal(f.fee_id)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-graphite px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid={`appeal-submit-${f.fee_id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Submit appeal</button>
                    <button onClick={() => { setAppealId(null); setReason(""); }} className="rounded-full px-3 py-1.5 text-xs font-semibold text-ink-soft hover:text-ink">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button onClick={() => { setAppealId(f.fee_id); setReason(""); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-graphite hover:underline" data-testid={`appeal-open-${f.fee_id}`}><RotateCcw className="h-3.5 w-3.5" /> Appeal once</button>
                  {daysLeft(f.appeal_deadline) !== null && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid={`appeal-countdown-${f.fee_id}`}><Clock className="h-3 w-3" /> {daysLeft(f.appeal_deadline) === 0 ? "Appeal window closing today" : `Appeal within ${daysLeft(f.appeal_deadline)} day${daysLeft(f.appeal_deadline) === 1 ? "" : "s"}`}</span>}
                </div>
              )
            )}
            {f.appealed && f.dispute_status === "open" && <p className="mt-1 text-xs font-semibold text-blue-700" data-testid={`appeal-pending-${f.fee_id}`}>Appeal under final review</p>}
            {!f.dispute_status && f.status !== "waived" && f.status !== "refunded" && (
              openId === f.fee_id ? (
                <div className="mt-2 space-y-2">
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why do you think this fee is unfair?" className="w-full rounded-lg border border-surface-300 p-2 text-xs" data-testid={`dispute-reason-${f.fee_id}`} />
                  <div className="flex items-center gap-2">
                    {photo ? (
                      <div className="flex items-center gap-2">
                        <img src={photo.preview} alt="evidence" className="h-12 w-12 rounded-lg border border-surface-200 object-cover" data-testid={`dispute-photo-preview-${f.fee_id}`} />
                        <button type="button" onClick={() => setPhoto(null)} className="text-xs font-semibold text-red-600 hover:underline">Remove</button>
                      </div>
                    ) : (
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-surface-300 px-3 py-1.5 text-xs font-semibold text-ink-soft hover:border-brand-200" data-testid={`dispute-photo-${f.fee_id}`}>
                        <ImagePlus className="h-3.5 w-3.5" /> Add photo
                        <input type="file" accept="image/*" onChange={onPickPhoto} className="hidden" />
                      </label>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => submitDispute(f.fee_id)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid={`dispute-submit-${f.fee_id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Flag className="h-3.5 w-3.5" />} Submit dispute</button>
                    <button onClick={() => { setOpenId(null); setReason(""); setPhoto(null); }} className="rounded-full px-3 py-1.5 text-xs font-semibold text-ink-soft hover:text-ink">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setOpenId(f.fee_id); setReason(""); }} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand hover:underline" data-testid={`dispute-open-${f.fee_id}`}><Flag className="h-3.5 w-3.5" /> Dispute this fee</button>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CancellationFeesCard;
